import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressLocationsLocationsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/locations-web',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/locations-web',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/locations-web',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/locations/v1/locations',
        destPath: '/v1/locations',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/locations-web',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/locations-web',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/locations/v1/locations',
        destPath: '/v1/locations',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_business-tools_locations';

/** Creates a location. */
export function createLocation(payload: object): RequestOptionsFactory<any> {
  function __createLocation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'location.address.geocode.latitude' },
          { path: 'location.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.CreateLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createLocation;
}

/** Creates locations in bulk. */
export function bulkCreateLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateLocation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'locations.address.geocode.latitude' },
          { path: 'locations.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.BulkCreateLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/bulk-create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'successfulLocations.address.geocode.latitude' },
              { path: 'successfulLocations.address.geocode.longitude' },
              { path: 'failedLocations.location.address.geocode.latitude' },
              { path: 'failedLocations.location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateLocation;
}

/** Retrieves a location. */
export function getLocation(payload: object): RequestOptionsFactory<any> {
  function __getLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.GetLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLocation;
}

/** Retrieves locations, given the specified filters, sorting, and paging. */
export function listLocations(payload: object): RequestOptionsFactory<any> {
  function __listLocations({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.ListLocations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'locations.address.geocode.latitude' },
              { path: 'locations.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listLocations;
}

/**
 * Overrides an existing location.
 *
 *
 * > **Note:** Currently, it isn't possible to partially update a location. Therefore, you'll need to pass the full location object in the body of the call.
 */
export function updateLocation(payload: object): RequestOptionsFactory<any> {
  function __updateLocation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'location.address.geocode.latitude' },
          { path: 'location.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'PUT' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.UpdateLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{location.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLocation;
}

/**
 * Creates a query to retrieve a list of locations.
 *
 *
 * The `queryLocations()` function builds a query to retrieve a list of up to 1,000 locations and returns a `LocationsQueryBuilder` object.
 *
 * The returned object contains the query definition which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `LocationsQueryBuilder` functions onto the query. `LocationsQueryBuilder` functions enable you to sort, filter, and control the results that `queryLocations()` returns. The functions that are chained to `queryLocations()` are applied in the order they are called.
 *
 * `queryLocations()` runs with the following `LocationsQueryBuilder` defaults that you can override:
 * - `skip`: `0`
 * - `limit`: `50`
 *
 * The following `QueryLocationsBuilder` functions are supported for the `queryLocations()` function. For a full description of the Locations object, see the object returned for the `items` property in `LocationsQueryResult`.
 */
export function queryLocations(payload: object): RequestOptionsFactory<any> {
  function __queryLocations({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.QueryLocations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'locations.address.geocode.latitude' },
              { path: 'locations.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryLocations;
}

/**
 * Sets a new default location.
 *
 *
 * > **Notes:**
 * > + There can only be one default location per site.
 * > + The default location can't be archived.
 */
export function setDefaultLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __setDefaultLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.SetDefaultLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{id}/set-default',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setDefaultLocation;
}

/**
 * Archives a location.
 *
 *
 * > **Notes:**
 * > + Changes the `archived` boolean of a location to `true`.
 * > + You can't change a location's `status` using this endpoint.
 * > + Archived locations can't be updated.
 * > + The `default` location can't be archived.
 */
export function archiveLocation(payload: object): RequestOptionsFactory<any> {
  function __archiveLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.ArchiveLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{id}/archive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __archiveLocation;
}

/** Adds a location type to the specified location. */
export function addTypeToLocation(payload: object): RequestOptionsFactory<any> {
  function __addTypeToLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.AddTypeToLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{id}/add-type',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addTypeToLocation;
}

/** Removes a location type from the specified location. */
export function removeTypeFromLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __removeTypeFromLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.locations.LocationsService.RemoveTypeFromLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{id}/remove-type',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeTypeFromLocation;
}

/**
 * Add location type to a specific location.
 *
 * > **Notes:**
 * > + Add one the location type (UNKNOWN, BRANCH, OFFICES, RECEPTION, HEADQUARTERS, INVENTORY) to a specific location by id.
 * > + This EP is deprecated - Please use AddTypeFromLocation instead.
 * @deprecated
 */
export function addLocationType(payload: object): RequestOptionsFactory<any> {
  function __addLocationType({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.AddLocationType',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{id}/add-location-type',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addLocationType;
}

/**
 * Remove location type from a specific location.
 *
 * > **Notes:**
 * > + Remove location type (UNKNOWN, BRANCH, OFFICES, RECEPTION, HEADQUARTERS, INVENTORY) from a specific location by id.
 * > + This EP is deprecated - Please use RemoveTypeFromLocation instead.
 * @deprecated
 */
export function removeLocationType(
  payload: object
): RequestOptionsFactory<any> {
  function __removeLocationType({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.locations.v1.location',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.locations.LocationsService.RemoveLocationType',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLocationsLocationsServiceUrl({
        protoPath: '/v1/locations/{id}/remove-location-type',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'location.address.geocode.latitude' },
              { path: 'location.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeLocationType;
}
